import { useState, useEffect } from 'react';
import { Form, Modal, Radio, Table, message } from 'antd';
import { useTranslation } from 'react-i18next';
import CustomButton from 'base/ui/components/Button';
import { SearchForm } from 'pages/Components/SearchBox';
import { TransitionButtonModel } from 'models/PurchaseOrder';
import { DeliveryInfoItem } from 'models/WasteHandoverRegistration';
import TareWeightModal from './TareWeightModal';

interface ConfirmHandoverModalProps {
    open: boolean;
    onClose: () => void;
    buttonInfo: TransitionButtonModel;
    taskId: string;
    wasteHandoverRegistrationCode?: string;
    deliveryInfoItems?: DeliveryInfoItem[];
    onUpdate: (tareData?: { numberOfTare: number; tareWeight: number; tareDeduction: number }, confirmedDetailIds?: string[], deliveryStatusesMap?: Record<string, string>) => Promise<boolean>;
    onExecute: (data: {
        taskId: string;
        statusTransitionId: string;
        deliveryStatuses?: Array<{
            wasteHandoverRegistrationDetailId: string;
            deliveryStatus: string;
        }>;
    }) => Promise<boolean>;
    onReload?: () => Promise<void>;
}

// Helper function để tạo key từ item
const getItemKey = (item: DeliveryInfoItem, index: number): string => {
    if (item.wasteHandoverRegistrationDetailId) {
        return item.wasteHandoverRegistrationDetailId;
    }
    if (item.stt) {
        return `stt_${item.stt}`;
    }
    return `index_${index}`;
};


const ConfirmHandoverModal: React.FC<ConfirmHandoverModalProps> = ({
    open,
    onClose,
    buttonInfo,
    taskId,
    wasteHandoverRegistrationCode,
    deliveryInfoItems = [],
    onUpdate,
    onExecute,
    onReload,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [tareWeightModalOpen, setTareWeightModalOpen] = useState(false);
    const [deliveryStatuses, setDeliveryStatuses] = useState<Record<string, string>>({});
    const [autoSelectedDBGKey, setAutoSelectedDBGKey] = useState<string | null>(null);

    // Khởi tạo deliveryStatuses từ deliveryInfoItems
    useEffect(() => {
        if (open && deliveryInfoItems.length > 0) {
            const initialStatuses: Record<string, string> = {};
            deliveryInfoItems.forEach((item, index) => {
                const key = getItemKey(item, index);
                // Dùng trực tiếp giá trị từ API, mặc định "CBG" nếu không có
                const status = item.deliveryStatus || 'CBG';
                initialStatuses[key] = status;
            });

            // Kiểm tra xem có item nào có trạng thái DBG từ lúc load không
            const hasInitialDBG = deliveryInfoItems.some(item => item.deliveryStatus === 'DBG');

            // Nếu chưa có DBG nào, tự động chọn DBG cho dòng có STT NHỎ NHẤT (dòng "đầu tiên") —
            // dùng STT nhỏ nhất để không phụ thuộc user đặt STT cụ thể, nhưng hành xử y như quy tắc "STT = 1":
            // chỉ tự gán khi dòng đó đang CHƯA BÀN GIAO (CBG), không đụng dòng đã TCBG/DBG.
            if (!hasInitialDBG && deliveryInfoItems.length > 0) {
                // Tìm dòng có STT nhỏ nhất
                let minIndex = 0;
                let minStt = deliveryInfoItems[0]?.stt ?? 0;
                deliveryInfoItems.forEach((item, idx) => {
                    const stt = item?.stt ?? 0;
                    if (stt < minStt) {
                        minStt = stt;
                        minIndex = idx;
                    }
                });

                const minItem = deliveryInfoItems[minIndex];
                if (minIndex >= 0 && (minItem?.deliveryStatus || 'CBG') === 'CBG') {
                    const minKey = getItemKey(minItem, minIndex);
                    initialStatuses[minKey] = 'DBG';
                    setAutoSelectedDBGKey(minKey);
                } else {
                    setAutoSelectedDBGKey(null);
                }
            } else {
                setAutoSelectedDBGKey(null);
            }

            setDeliveryStatuses(initialStatuses);
        } else if (!open) {
            // Reset khi modal đóng
            setDeliveryStatuses({});
            setAutoSelectedDBGKey(null);
        }
    }, [open, deliveryInfoItems]);

    // Xử lý thay đổi trạng thái bàn giao
    const handleStatusChange = (detailId: string, status: string) => {
        setDeliveryStatuses((prev) => ({
            ...prev,
            [detailId]: status,
        }));
    };

    // Xử lý submit
    const handleSubmit = async () => {
        setLoading(true);
        try {
            // Kiểm tra xem có item nào được chọn "XN bàn giao" mới không (không tính các item đã DBG từ lúc load)
            // Tính cả item được tự động chọn như là quyết định mới của user
            const newlyConfirmedItems = Object.entries(deliveryStatuses).filter(([key, status]) => {
                if (status !== 'DBG') return false;
                // Tìm item tương ứng trong deliveryInfoItems để kiểm tra xem nó có phải DBG từ lúc load không
                const item = deliveryInfoItems.find((item, idx) => {
                    const itemKey = getItemKey(item, idx);
                    return itemKey === key;
                });
                // Chỉ tính nếu item đó KHÔNG phải là DBG từ lúc load (tức là user vừa chọn hoặc được tự động chọn)
                return item && item.deliveryStatus !== 'DBG';
            });

            if (newlyConfirmedItems.length > 0) {
                // Nếu có item được chọn "XN bàn giao" mới, mở modal "Ghi nhận trừ bì"
                setTareWeightModalOpen(true);
                setLoading(false);
            } else {
                // Nếu không có item nào được chọn mới, call update và execute transition luôn
                // Truyền deliveryStatuses để cập nhật vào form
                const deliveryStatusesMap = Object.entries(deliveryStatuses)
                    .filter(([key]) => !key.startsWith('stt_') && !key.startsWith('index_'))
                    .reduce((acc, [detailId, status]) => {
                        acc[detailId] = status;
                        return acc;
                    }, {} as Record<string, string>);

                const updateSuccess = await onUpdate(undefined, undefined, deliveryStatusesMap);
                if (!updateSuccess) {
                    setLoading(false);
                    return;
                }

                // Map deliveryStatuses về format API, chỉ lấy các key là wasteHandoverRegistrationDetailId thực sự
                const executeData: any = {
                    taskId,
                    statusTransitionId: buttonInfo.statusTransitionId,
                    deliveryStatuses: Object.entries(deliveryStatuses)
                        .filter(([key]) => !key.startsWith('stt_') && !key.startsWith('index_')) // Chỉ lấy các key là ID thực sự
                        .map(([detailId, status]) => ({
                            wasteHandoverRegistrationDetailId: detailId,
                            deliveryStatus: status, // Dùng trực tiếp giá trị từ radio
                        })),
                };

                const executeSuccess = await onExecute(executeData);

                if (executeSuccess) {
                    handleClose();
                    // Reload sau khi đóng modal để tránh nháy trạng thái radio
                    if (onReload) {
                        await onReload();
                    }
                } else {
                    setLoading(false);
                }
            }
        } catch (error) {
            console.error('Error executing transition button:', error);
            message.error(t('message.error'));
            setLoading(false);
        }
    };

    // Xử lý sau khi lưu modal "Ghi nhận trừ bì"
    const handleTareWeightSave = async (tareData: { numberOfTare: number; tareWeight: number; tareDeduction: number }) => {
        setLoading(true);
        try {
            // Lấy danh sách các item được chọn "XN bàn giao" (DBG)
            const confirmedDetailIds = Object.entries(deliveryStatuses)
                .filter(([key, status]) => status === 'DBG' && !key.startsWith('stt_') && !key.startsWith('index_'))
                .map(([detailId]) => detailId);

            // Tạo deliveryStatusesMap để cập nhật deliveryStatus
            const deliveryStatusesMap = Object.entries(deliveryStatuses)
                .filter(([key]) => !key.startsWith('stt_') && !key.startsWith('index_'))
                .reduce((acc, [detailId, status]) => {
                    acc[detailId] = status;
                    return acc;
                }, {} as Record<string, string>);

            // Bước 1: Update form trước với tare data (noPacking, perPackingWeight, packingWeight)
            // Chỉ áp dụng cho các item được chọn "XN bàn giao"
            const updateSuccess = await onUpdate(tareData, confirmedDetailIds, deliveryStatusesMap);
            if (!updateSuccess) {
                setLoading(false);
                return;
            }

            // Bước 2: Thực thi transition với deliveryStatuses
            // Map deliveryStatuses về format API, chỉ lấy các key là wasteHandoverRegistrationDetailId thực sự
            const executeData: any = {
                taskId,
                statusTransitionId: buttonInfo.statusTransitionId,
                deliveryStatuses: Object.entries(deliveryStatuses)
                    .filter(([key]) => !key.startsWith('stt_') && !key.startsWith('index_')) // Chỉ lấy các key là ID thực sự
                    .map(([detailId, status]) => ({
                        wasteHandoverRegistrationDetailId: detailId,
                        deliveryStatus: status, // Dùng trực tiếp giá trị từ radio
                    })),
            };

            const executeSuccess = await onExecute(executeData);

            if (executeSuccess) {
                handleClose();
                // Reload sau khi đóng modal để tránh nháy trạng thái radio
                if (onReload) {
                    await onReload();
                }
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.error('Error executing transition button:', error);
            message.error(t('message.error'));
            setLoading(false);
        }
    };

    const handleClose = () => {
        form.resetFields();
        setDeliveryStatuses({});
        setAutoSelectedDBGKey(null);
        setTareWeightModalOpen(false);
        onClose();
    };

    // Reset form khi modal mở/đóng
    useEffect(() => {
        if (!open) {
            form.resetFields();
            setDeliveryStatuses({});
            setAutoSelectedDBGKey(null);
            setTareWeightModalOpen(false);
        }
    }, [open, form]);

    // Kiểm tra có thay đổi mới sang DBG hoặc TCBG so với trạng thái ban đầu không
    // Tính cả item được tự động chọn như là quyết định mới của user
    const hasNewDecision = deliveryInfoItems.some((item, index) => {
        const key = getItemKey(item, index);
        const initialStatus = item.deliveryStatus ?? 'CBG';
        const currentStatus = deliveryStatuses[key] ?? initialStatus;
        // Chỉ bật khi có thay đổi và chọn DBG hoặc TCBG mới (tính cả item được tự động chọn)
        return (currentStatus === 'DBG' || currentStatus === 'TCBG') && currentStatus !== initialStatus;
    });

    // Dòng "đầu tiên" = dòng có STT NHỎ NHẤT (dùng STT nhỏ nhất để không phụ thuộc user đặt STT cụ thể).
    const firstSttRowIndex = (() => {
        if (!deliveryInfoItems.length) return -1;
        let minIdx = 0;
        let minStt = deliveryInfoItems[0]?.stt ?? 0;
        deliveryInfoItems.forEach((item, idx) => {
            const stt = item?.stt ?? 0;
            if (stt < minStt) {
                minStt = stt;
                minIdx = idx;
            }
        });
        return minIdx;
    })();
    // Quy tắc: khóa các dòng còn lại khi dòng STT nhỏ nhất CHƯA được xử lý từ trước (deliveryStatus gốc = CBG),
    // ép user quyết định STT1 (XN bàn giao / Từ chối) TRƯỚC rồi Lưu; các mặt hàng còn lại xử lý ở lần mở modal SAU.
    // (ĐÃ BỎ: mở khóa ngay khi từ chối STT1 — user KHÔNG quyết định nhiều mặt hàng cùng lúc trên 1 modal.)
    const isFirstSttStillPending =
        firstSttRowIndex >= 0
        && (deliveryInfoItems[firstSttRowIndex]?.deliveryStatus ?? 'CBG') === 'CBG';

    // Columns cho table
    const columns = [
        {
            title: 'STT',
            dataIndex: 'stt',
            key: 'stt',
            width: 60,
            align: 'center' as const,
        },
        {
            title: 'Chất thải/ Phế liệu',
            dataIndex: 'productName',
            key: 'productName',
            render: (_: any, record: DeliveryInfoItem) => {
                return `${record.productCode || ''} | ${record.productName || ''}`;
            },
        },
        {
            title: 'Trạng thái bàn giao',
            key: 'deliveryStatus',
            width: 350,
            render: (_: any, record: DeliveryInfoItem, index: number) => {
                // Sử dụng cùng logic key như trong useEffect
                const key = getItemKey(record, index);
                // Ưu tiên lấy từ deliveryStatuses (đã được set trong useEffect), sau đó từ record.deliveryStatus, cuối cùng mặc định 'CBG'
                const currentStatus = deliveryStatuses[key] ?? record.deliveryStatus ?? 'CBG';

                // Kiểm tra xem item này đã là "XN bàn giao" (DBG) khi load lên không
                const isInitialDBG = record.deliveryStatus === 'DBG';
                // Kiểm tra xem item này đã là "Từ chối bàn giao" (TCBG) khi load lên không
                const isInitialTCBG = record.deliveryStatus === 'TCBG';
                // Nếu dòng STT đầu tiên lúc mở modal còn CBG (chưa bàn giao) thì disable toàn bộ các dòng còn lại
                const isBlockedByFirstStt = isFirstSttStillPending && firstSttRowIndex >= 0 && index !== firstSttRowIndex;
                // Kiểm tra xem item này có được tự động chọn thành DBG không
                const isAutoSelectedDBG = autoSelectedDBGKey === key;
                // Kiểm tra xem có item nào khác mà user vừa chọn DBG không (không tính các item đã là DBG từ lúc load)
                // Chỉ kiểm tra trong deliveryStatuses những item mà:
                // - Có status là DBG trong deliveryStatuses
                // - VÀ không phải là DBG từ lúc load lên (tức là user vừa chọn hoặc được tự động chọn)
                const hasOtherDBG = Object.entries(deliveryStatuses).some(([otherKey, otherStatus]) => {
                    if (otherKey === key || otherStatus !== 'DBG') return false;
                    // Tìm item tương ứng trong deliveryInfoItems để kiểm tra xem nó có phải DBG từ lúc load không
                    const otherItem = deliveryInfoItems.find((item, idx) => {
                        const itemKey = getItemKey(item, idx);
                        return itemKey === otherKey;
                    });
                    // Chỉ tính nếu item đó KHÔNG phải là DBG từ lúc load (tức là user vừa chọn hoặc được tự động chọn)
                    return otherItem && otherItem.deliveryStatus !== 'DBG';
                });
                // Disable toàn bộ nếu item này đã là DBG hoặc TCBG khi load lên
                const isRowDisabled = isInitialDBG || isInitialTCBG || isBlockedByFirstStt || loading;
                // Disable radio "XN bàn giao" nếu có item khác mà user vừa chọn DBG và item hiện tại không phải DBG
                const isDBGDisabled = hasOtherDBG && currentStatus !== 'DBG';
                // Lần bàn giao đầu tiên: mặc định DBG, chỉ khóa "Chưa bàn giao"; vẫn cho chọn "Từ chối"
                const isCBGDisabled = isAutoSelectedDBG;
                const isTCBGDisabled = false;

                return (
                    <Radio.Group
                        value={currentStatus}
                        onChange={(e) => handleStatusChange(key, e.target.value)}
                        disabled={isRowDisabled}
                        className="flex flex-col gap-2 [&_.ant-radio-disabled]:!opacity-60"
                    >
                        <Radio value="CBG" disabled={isCBGDisabled || isRowDisabled}>Chưa bàn giao</Radio>
                        <Radio value="DBG" disabled={isDBGDisabled || isRowDisabled}>XN bàn giao</Radio>
                        <Radio value="TCBG" disabled={isTCBGDisabled || isRowDisabled}>Từ chối</Radio>
                    </Radio.Group>
                );
            },
        },
    ];

    return (
        <>
            <Modal
                open={open}
                onCancel={handleClose}
                footer={null}
                title={<h2 className="uppercase text-center text-base font-semibold">XÁC NHẬN BÀN GIAO</h2>}
                width={900}
                className="[&_.ant-modal-header]:!border-b-0 [&_.ant-modal-title]:!text-center"
            >
                <SearchForm
                    form={form}
                    onFinish={handleSubmit}
                    column="col-1"
                >
                    <div className="mb-4">
                        <Table
                            columns={columns}
                            dataSource={deliveryInfoItems}
                            rowKey={(record) => record.wasteHandoverRegistrationDetailId || record.stt?.toString() || ''}
                            pagination={false}
                            size="small"
                            bordered
                            className="[&_.ant-table-thead>tr>th]:!bg-[#f5f5f5] [&_.ant-table-thead>tr>th]:!font-semibold [&_.ant-table-thead>tr>th]:!text-black [&_.ant-table-thead>tr>th]:!border-gray-300 [&_.ant-table-thead>tr>th]:!text-center"
                        />
                    </div>

                    {/* Buttons */}
                    <div className="mt-4 flex justify-end gap-2">
                        <CustomButton
                            type="default"
                            htmlType="button"
                            loading={loading}
                            onClick={handleClose}
                        >
                            Thoát
                        </CustomButton>
                        <CustomButton
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            disabled={!hasNewDecision}
                        >
                            Lưu
                        </CustomButton>
                    </div>
                </SearchForm>
            </Modal>

            {/* Modal "Ghi nhận trừ bì" */}
            <TareWeightModal
                open={tareWeightModalOpen}
                onClose={() => setTareWeightModalOpen(false)}
                onSave={handleTareWeightSave}
                loading={loading}
            />
        </>
    );
};

export default ConfirmHandoverModal;

